import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';
import parse from 'html-react-parser';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import Form from '../../../../lib/wrappers/Form';
import UniversalInput from '../../../../lib/wrappers/UniversalInput';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { IUserInfo } from '../../types/ridpTypes';
import { environmentSelector } from '../../../../selectors/commonSelectors';
import { StyledIdentityHeader } from '../Layout';
import AlertMessage from '../../../../components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import GIButton from '../../../../components/common/button/GIButton';
import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import { INPUT_TYPES } from '../../../../constants/inputs/inputTypes';
import { DROPDOWN_OPTIONS } from '../../../../constants/ssap/hhmTypes/dropdownOptions';
import { VALIDATION_TYPES } from '../../../../constants/inputs/validationTypes';
import { setResponseBasedErrorMessage, uploadDocuments } from '../../../../actions/ridp/ridpActionCreators';
import endpoints from '../../../../constants/configs/endpoints';
import { configs } from '../../../../constants/configs/configs';
import { BREAK_POINTS } from '../../../../constants/common/breakpoints';

const StyledUploadDocumentContainer = styled.div`
    .gi-alert {
        padding: 10px;
        margin-top: 20px !important;
        margin-bottom: 20px !important;

        .usa-alert__text {
            h5 {
                font-weight: 400;
                font-size: 18px;
                margin-top: 5px;
                margin-bottom: 10px;
            }
        }
    }
    .gi-dropdown {
        margin: 0;
        margin-top: 30px;
        display: flex;

        > label {
            font-size: 16px;
            color: ${({ theme }) => theme.gray2};
            text-align: right;
            @media (max-width: ${BREAK_POINTS.sm}) {
                text-align: left;
                width: fit-content;
            }
        }
        > select {
            height: 35px !important;
            font-size: 14px;
            border: ${({ theme }) => `1px solid ${theme.gray4}`};
            border-radius: 2px;
            color: ${({ theme }) => theme.gray3};
        }
    }
`;

const StyledParagraph = styled.p`
    margin: 0;
    font-size: 16px;
    margin-bottom: 10px;
`;

const StyledUploadDocument = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 30px;
    @media (max-width: ${BREAK_POINTS.sm}) {
        width: fit-content;
        flex-direction: column !important;
    }
    @media (max-width: ${BREAK_POINTS.xl}) {
        flex-direction: row;
    }
`;

const StyledLabel = styled.label`
    font-weight: 400 !important;
    width: fit-content;
    font-size: 16px;
    color: ${({ theme }) => theme.primaryMain};
    padding: 8px 16px;
    background-color: ${({ theme }) => theme.defaultWhite};
    border: ${({ theme }) => `1px solid ${theme.primaryMain}`};
    border-radius: 4px;
    cursor: pointer;
    margin-right: 12px;
    font-size: 14px;
    height: 36px;
`;

const StyledInputUpload = styled.input`
    display: none !important;
    height: 100% !important;
`;

const StyledDisabledInput = styled.input`
    border-bottom: 1px solid #d3d3d3;
    box-shadow: none;
    font-size: 14px;
    -webkit-border-radius: 2px;
    -moz-border-radius: 2px;
    border-radius: 2px;
    border: ${({ theme }) => `1px solid ${theme.gray4}`};
    height: 30px;
    width: 300px;
    @media (max-width: ${BREAK_POINTS.sm}) {
        margin-top: 20px;
    }
`;
const StyledTable = styled.table`
    border: none;
    margin-top: 10px;
    font-size: 14px;
    width: 100%;
`;

const StyledHeaderContainer = styled.thead``;

const StyledHeaderRow = styled.tr``;

const StyledHeader = styled.th`
    background-color: ${( {theme} ) => theme.gray9};
    color: ${( {theme} ) => theme.gray3};
    padding: 8px;
    font-weight: 600;
`;
const StyledBody = styled.tbody`
    > tr:nth-child(odd) {
        background-color: ${( {theme} ) => theme.gray11};
    }

    > tr:nth-child(even) {
        background-color: ${( {theme} ) => theme.defaultWhite};
    }
`;

const StyledRow = styled.tr``;

const StyledColumn = styled.td<{ width?: number }>`
    width: ${( {width} ) => width}%;
    padding: 8px;

    .gi-alert {
        padding: 5px;
        width: fit-content;
        border-radius: 2px;

        > div {
            padding-left: 0px !important;
            font-size: 12px;
        }
    }

    .doctype {
        font-size: 11px !important;
        color: ${( {theme} ) => theme.gray5};
    }
`;

const StyledInput = styled.div`
    width: fit-content;
`;

const StyledLabelName = styled.label`
    font-weight: 400 !important;
    width: fit-content;
    font-size: 16px;
    color: ${({ theme }) => theme.gray2};
    padding-right: 16px;
    padding-left: 16px;
    @media (max-width: ${BREAK_POINTS.sm}) {
        padding-left: 0;
        width: 100%;
        margin-bottom: 10px;
    }
`;

const StyledSpan = styled.span`
    font-weight: 400 !important;
    width: fit-content;
    font-size: 16px;
    color: ${( {theme} ) => theme.gray2};
    padding-right: 16px;
    padding-left: 16px;
    @media (max-width: ${BREAK_POINTS.sm}) {
        padding-left: 0;
        width: 100%;
        margin-bottom: 10px;
    }
`;

const StyledButton = styled.div`
    width: 100%;
    display: flex;
    justify-content: end;
    padding-right: 25px;
    padding-bottom: 25px;
    padding-top: 25px;
`;
const ALLOWED_FILE_TYPES = [
    'image/bmp',
    'image/gif',
    'image/jpeg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/pdf',
];

const ALLOWED_EXT = '.bmp,.gif,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.pdf';

const ManualVerification = () => {
    const dispatch: Dispatch<any> = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const existingParams = new URLSearchParams(window.location.search);
    const environment = useAppSelector(environmentSelector);
    const {
        routes: {RIDP_UPLOAD_DOCUMENT_PROCESS, RIDP_VERIFICATION_REJECTED, RIDP_UPLOAD_DOCUMENT},
    } = configs;

    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [documentType, setDocumentType] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const responseBasedErrorMessage = useAppSelector(state => state.ridp.responseBasedErrorMessage);
    const currentUserInfo = useAppSelector(state => state.ridp.currentUserInfo) as IUserInfo;
    const viewRef = useAppSelector(state => state.ridp.viewRef);

    useEffect(() => {
        if (
            currentUserInfo &&
            Array.isArray(currentUserInfo?.documentsUploaded) &&
            currentUserInfo?.documentsUploaded.length > 0 &&
            currentUserInfo.ridpSource === 'MANUAL'
        ) {
            setIsSubmitting(false);
            const hasSubmitted = currentUserInfo.documentsUploaded.some(doc => doc.status === 'SUBMITTED');

            if (hasSubmitted) {
                navigate(`${RIDP_UPLOAD_DOCUMENT_PROCESS}?${existingParams.toString()}`);
            } else {
                navigate(`${RIDP_VERIFICATION_REJECTED}?${existingParams.toString()}`);
            }
        }
    }, [currentUserInfo]);

    const handleFileChange = useCallback(
        event => {
            const file = event.target.files[0];
            if (file) {
                if (ALLOWED_FILE_TYPES.includes(file.type)) {
                    setSelectedFile(file);
                    setFileName(file.name);
                    dispatch(setResponseBasedErrorMessage(''));
                } else {
                    dispatch(setResponseBasedErrorMessage('ridp.manualverification.error.uploaddocument'));
                }
            }
        },
        [setSelectedFile, setFileName],
    );

    const handleSubmit = useCallback(
        event => {
            event.preventDefault();
            setIsSubmitting(true);
            const formData = new FormData();
            if (selectedFile) {
                formData.append('manualVerificationFile', selectedFile as any);
            }
            formData.append('doctype', documentType);
            formData.append('csrftoken', window.csrf_token);

            dispatch(
                uploadDocuments(
                    endpoints.RIDP_MANUAL_VERIFICATION_ENDPOINT.processReplacements({
                        viewRef,
                    }),
                    formData,
                    environment,
                    viewRef,
                ),
            );
        },
        [selectedFile, documentType],
    );

    const onFieldChange = useCallback(
        (newData): void => {
            setDocumentType(newData.documentType);
        },
        [setDocumentType],
    );

    const isButtonDisabled = useMemo(() => {
        return !documentType || documentType === 'Select' || !selectedFile;
    }, [documentType, selectedFile]);
    const documentData = useMemo(() => {
        return Array.isArray(currentUserInfo?.documentsUploaded) ? currentUserInfo.documentsUploaded : [];
    }, [ currentUserInfo ]);

    return (
        <div id="main-content">
        <StyledUploadDocumentContainer>
                <ConditionalRendering isTrue={responseBasedErrorMessage}>
                <AlertMessage
                    showIcon={false}
                    content={<FormattedMessage id={responseBasedErrorMessage} />}
                    type="error"
                />
                </ConditionalRendering>
            <StyledIdentityHeader fontWeight={500}>
                <FormattedMessage id={'ridp.manualverification.header'} />
            </StyledIdentityHeader>
                <ConditionalRendering isTrue={location.pathname.includes(RIDP_UPLOAD_DOCUMENT)}>
                    <AlertMessage
                        showIcon={false}
                        content={parse(
                            intl.formatMessage({id: 'ridp.manualverification.uploaddocument.alert'}),
                        )}
                type="error"
            />

            <StyledParagraph>
                <FormattedMessage id="ridp.manualverification.uploaddocument.description" />
            </StyledParagraph>
                </ConditionalRendering>
                <ConditionalRendering isTrue={location.pathname.includes(RIDP_VERIFICATION_REJECTED)}>
                    <AlertMessage
                        showIcon={false}
                        content={parse(
                            intl.formatMessage({id: 'ridp.manualverification.rejected.alert'}),
                        )}
                        type="error"
                    />
                </ConditionalRendering>
            <Form {...{ formName: 'ridpuploaddocument', action: {} }}>
                <UniversalInput
                    label={{
                        text: 'ridp.manualverification.document.input.label',
                        isLabelRequired: false,
                    }}
                    inputData={{
                        isInputRequired: true,
                        type: INPUT_TYPES.DROPDOWN,
                        name: 'documentType',
                        value: documentType,
                        options: DROPDOWN_OPTIONS.RIDP_VERIFICATION_DOCUMENT_OPTIONS,
                        currentData: {
                            documentType,
                        },
                        fields: [],
                        isInputStandalone: true,
                        validationType: VALIDATION_TYPES.CHOSEN,
                        inputClass: ['small-width'],
                    }}
                    inputActions={{
                        onFieldChange,
                    }}
                    shouldParse
                />

                <StyledUploadDocument>
                        <StyledSpan className="required-true">
                        <FormattedMessage id="ridp.manualverification.uploaddocument.label" />
                        </StyledSpan>
                    <StyledInput>
                            <StyledLabel
                                htmlFor="fileUpload"
                                style={{fontWeight: 'bold'}}
                                data-testid="ridp-file-upload-btn"
                                id="ridp-file-upload-btn"
                            >
                            <FormattedMessage id="ridp.manualverification.uploaddocument.label" />
                        </StyledLabel>
                        <StyledInputUpload
                            type="file"
                            id="fileUpload"
                            onChange={handleFileChange}
                            style={{ display: 'block', margin: '10px 0' }}
                            className="file-input"
                            accept={ALLOWED_EXT}
                        />
                        <StyledDisabledInput
                            type="text"
                            name="chooseFile"
                                data-testid="ridp-file-upload-select"
                            disabled
                            placeholder={
                                fileName || intl.formatMessage({ id: 'ridp.manualverification.fileplaceholder' })
                            }
                                aria-label="Choose file"
                        />
                    </StyledInput>
                </StyledUploadDocument>
                <StyledButton>
                    <GIButton
                        type="submit"
                        id="ridpManualVerification"
                        labelId="button.submit"
                        onClickHandler={e => handleSubmit(e)}
                        isDisabled={isButtonDisabled || isSubmitting}
                    />
                </StyledButton>
            </Form>
                <ConditionalRendering isTrue={location.pathname.includes(RIDP_VERIFICATION_REJECTED)}>
                    <StyledTable>
                        <StyledHeaderContainer>
                            <StyledHeaderRow>
                                <StyledHeader>
                                    <FormattedMessage id={'ridp.manualverification.document.label'}/>
                                </StyledHeader>
                                <StyledHeader>
                                    <FormattedMessage id={'ridp.manualverification.status.label'}/>
                                </StyledHeader>
                                <StyledHeader>
                                    <FormattedMessage id={'ridp.manualverification.notes.label'}/>
                                </StyledHeader>
                            </StyledHeaderRow>
                        </StyledHeaderContainer>
                        <StyledBody>
                            {documentData.map(( document, index ) => (
                                <StyledRow key={index.toString()}>
                                    <StyledColumn className="document-name" width={30}>
                                        {document?.documentType} <br/>
                                        <span className="doctype">{document?.documentName}</span>
                                    </StyledColumn>
                                    <StyledColumn>
                                        {' '}
                                        <AlertMessage showIcon={false} content={document?.status} type="info"/>
                                    </StyledColumn>
                                    <StyledColumn width={50}>{document?.comments}</StyledColumn>
                                </StyledRow>
                            ))}
                        </StyledBody>
                    </StyledTable>
                </ConditionalRendering>
        </StyledUploadDocumentContainer>
        </div>
    );
};

export default ManualVerification;